import { useCallback, useEffect, useRef, useState } from "react";

export type MeasuredSize = { width: number; height: number };

/**
 * Measures an element's border-box size (padding included, like a `width` px
 * override) with a ResizeObserver. Returns a callback ref (so it re-attaches
 * when the element mounts/unmounts, e.g. a column that can be hidden) and the
 * latest rounded size (`null` until measured).
 */
export function useMeasuredSize<T extends HTMLElement = HTMLElement>(): [
  (node: T | null) => void,
  MeasuredSize | null,
] {
  const [size, setSize] = useState<MeasuredSize | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const box = entry.borderBoxSize?.[0];
        const rect =
          box && typeof box.inlineSize === "number"
            ? { width: box.inlineSize, height: box.blockSize }
            : entry.target.getBoundingClientRect();
        const next = {
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        };
        setSize((prev) =>
          prev && prev.width === next.width && prev.height === next.height
            ? prev
            : next,
        );
      }
    });
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, size];
}

/** Width-only convenience over `useMeasuredSize`. */
export function useMeasuredWidth<T extends HTMLElement = HTMLElement>(): [
  (node: T | null) => void,
  number | null,
] {
  const [ref, size] = useMeasuredSize<T>();
  return [ref, size?.width ?? null];
}
